// CTW-DURMAH-STUDENT-MANUAL-01:
// Manual search. Dependency-free token-overlap scorer following the house
// pattern established in src/lib/durmah/intelligence/supportPack.ts, so the
// same engine can serve the browser UI and Durmah's grounded answers.
//
// Deliberately NOT a fuzzy matcher. Students search for the words that are
// on screen; the `keywords` array on each section carries the synonyms and
// plain-English phrasings that the body text does not. That covers real
// query variation without the false positives a trigram matcher produces on
// a corpus this small.

import {
  MANUAL_SECTIONS,
  sectionPlainText,
  type ManualCategoryId,
  type ManualSection,
} from "@/content/manual";

export interface ManualSearchHit {
  section: ManualSection;
  score: number;
  /** A short body extract around the first match, for the result list. */
  excerpt: string;
}

/* ------------------------------------------------------------------ */
/* Tokenising                                                          */
/* ------------------------------------------------------------------ */

/** Words too common to carry signal in a study-app manual. */
const STOP_WORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "but", "by", "can", "do", "does",
  "for", "from", "get", "how", "i", "if", "in", "is", "it", "its", "me", "my",
  "of", "on", "or", "so", "that", "the", "then", "there", "this", "to", "use",
  "was", "what", "when", "where", "which", "why", "will", "with", "you", "your",
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOP_WORDS.has(t));
}

/**
 * Light stemming: collapse the plural/gerund variants students actually type
 * ("uploads", "uploading" → "upload"). Cheap and predictable.
 */
function stem(token: string): string {
  if (token.length > 5 && token.endsWith("ing")) return token.slice(0, -3);
  if (token.length > 4 && token.endsWith("es")) return token.slice(0, -2);
  if (token.length > 3 && token.endsWith("s") && !token.endsWith("ss")) {
    return token.slice(0, -1);
  }
  return token;
}

function tokenSet(text: string): Set<string> {
  return new Set(tokenize(text).map(stem));
}

function overlap(query: Set<string>, values: string[]): number {
  const stemmed = new Set(values.map(stem));
  let hits = 0;
  for (const term of query) if (stemmed.has(term)) hits += 1;
  return hits;
}

/* ------------------------------------------------------------------ */
/* Excerpting                                                          */
/* ------------------------------------------------------------------ */

const EXCERPT_LENGTH = 180;

function buildExcerpt(section: ManualSection, rawQuery: string): string {
  const body = sectionPlainText(section);
  const needle = rawQuery.trim().toLowerCase();

  if (needle.length > 2) {
    const at = body.toLowerCase().indexOf(needle);
    if (at >= 0) {
      const start = Math.max(0, at - 60);
      const slice = body.slice(start, start + EXCERPT_LENGTH).trim();
      return `${start > 0 ? "…" : ""}${slice}${
        start + EXCERPT_LENGTH < body.length ? "…" : ""
      }`;
    }
  }

  return section.summary;
}

/* ------------------------------------------------------------------ */
/* Search                                                              */
/* ------------------------------------------------------------------ */

export interface ManualSearchOptions {
  /** Restrict to one category. */
  category?: ManualCategoryId | null;
  /**
   * When false, member-only sections are still returned (so a signed-out
   * visitor can discover that the answer exists and sign in for it) but the
   * caller must render them as gated. Set true to drop them entirely.
   */
  excludeMemberOnly?: boolean;
  limit?: number;
}

/**
 * Score every section against a query.
 *
 * Weighting mirrors the house support-pack scorer:
 *   exact title match      +40   (a student typing the exact title wants it)
 *   title token            ×8
 *   keyword token          ×5    (curated synonyms — high signal)
 *   tag token              ×3
 *   summary token          ×2
 *   body token             ×1
 *   whole-phrase in body   +6
 */
export function searchManual(
  query: string,
  options: ManualSearchOptions = {},
): ManualSearchHit[] {
  const { category = null, excludeMemberOnly = false, limit = 20 } = options;
  const trimmed = query.trim();

  const pool = MANUAL_SECTIONS.filter((s) => {
    if (category && s.category !== category) return false;
    if (excludeMemberOnly && s.audience === "member") return false;
    return true;
  });

  // No query: return the pool in reading order, unscored.
  if (trimmed.length === 0) {
    return pool
      .slice(0, limit)
      .map((section) => ({ section, score: 0, excerpt: section.summary }));
  }

  const terms = tokenSet(trimmed);
  const phrase = trimmed.toLowerCase();

  const scored = pool.map((section) => {
    let score = 0;

    if (section.title.toLowerCase() === phrase) score += 40;

    score += overlap(terms, tokenize(section.title)) * 8;
    score += overlap(terms, section.keywords.flatMap(tokenize)) * 5;
    score += overlap(terms, section.tags.flatMap(tokenize)) * 3;
    score += overlap(terms, tokenize(section.summary)) * 2;

    const body = sectionPlainText(section);
    score += overlap(terms, tokenize(body));

    if (phrase.length > 3 && body.toLowerCase().includes(phrase)) score += 6;

    // Gentle bias towards earlier sections when scores tie, so "getting
    // started" content wins over a deep reference page for a vague query.
    score += Math.max(0, 3 - section.order) * 0.1;

    return { section, score, excerpt: buildExcerpt(section, trimmed) };
  });

  return scored
    .filter((hit) => hit.score > 0)
    .sort((a, b) => b.score - a.score || a.section.order - b.section.order)
    .slice(0, limit);
}

/**
 * Compact result shape for Durmah's grounded answers.
 * Returns the few best sections with a link, so the assistant can answer from
 * the manual and cite where it came from rather than improvising.
 */
export interface ManualGroundingHit {
  title: string;
  href: string;
  summary: string;
  /** Section body, trimmed to keep the prompt bounded. */
  body: string;
  audience: "public" | "member";
}

const GROUNDING_BODY_MAX_CHARS = 1200;

export function searchManualForGrounding(
  query: string,
  limit = 3,
): ManualGroundingHit[] {
  return searchManual(query, { limit }).map(({ section }) => {
    const body = sectionPlainText(section);
    return {
      title: section.title,
      href: `/manual/${section.slug}`,
      summary: section.summary,
      body:
        body.length > GROUNDING_BODY_MAX_CHARS
          ? `${body.slice(0, GROUNDING_BODY_MAX_CHARS)}…`
          : body,
      audience: section.audience,
    };
  });
}
